import { Guest } from "@/types/Guest";
import {
  getGuestsByIds,
  GUEST_REFETCH_FIELDS,
} from "@/lib/api/handlers/GuestHandling";

/**
 * Finds out what an automation did to a guest, after the fact.
 *
 * A cockpit write returns as soon as the workflow message is ENQUEUED — the
 * consumer applies the `UPDATE_GUEST` afterwards, in another service, with no
 * channel back to the browser. Without this, the row keeps its pre-automation
 * value until the user reloads the page.
 *
 * One module-level poller serves every surface. The alternative — a hook per
 * provider — would mean N timers and N fetches for the same guest whenever the
 * guest list, the detail popup and the companions sidebar are mounted together,
 * and those live in disconnected provider trees (plus isolated `GuestsProvider`
 * instances) that cannot share React state. The repo already solves cross-tree
 * fan-out with window events (`GuestsProvider.tsx` dispatches `guestDataUpdated`
 * for exactly this reason), so: arm → one shared wave → broadcast → N listeners.
 *
 * What this is NOT: a prediction. The scheduler never invents a value; it only
 * re-reads the guest and reports what the server actually stored. That matters
 * most for `state`, whose write can be REFUSED server-side by the capacity CAS.
 */

/** One watched field on one guest. Satisfied when EITHER test passes. */
export interface FieldWatch {
  /** Canonical/bare field name, matching `OverwriteEntry.field`. */
  field: string;
  isAdditional: boolean;
  /** Value at arm time. Moving away from it means the worker ran. */
  fromRaw?: unknown;
  /** Predicted value. Matching it means the worker ran — the only test
   *  available to a surface that no longer holds a baseline. */
  toRaw?: unknown;
}

export interface ArmRequest {
  eventId: string;
  /** guestId → fields to watch on THAT guest. */
  watches: Map<string, FieldWatch[]>;
  tier?: "full" | "short";
}

export interface GuestReconciledDetail {
  eventId: string;
  guestId: string;
  guest: Guest;
}

export interface GuestReconcileCoarseDetail {
  eventId: string;
}

/** Same cadence the guest-detail popup used before this was shared out. */
export const WORKFLOW_RECONCILE_DELAYS = [
  1000, 1500, 2000, 2500, 3000, 4000, 5000, 6000, 8000, 10000,
];
/** Used when we have no watch to terminate on — keep it brief. */
export const SHORT_RECONCILE_DELAYS = [1000, 1500, 2000];
/** Beyond this many guests, stop tracking individuals and pulse coarsely. */
export const MAX_WATCHED_GUESTS = 200;
/** Ids per request. */
export const RECONCILE_CHUNK = 100;
/** Per-guest budget; a guest still unsatisfied after this is given up on. */
export const RECONCILE_DEADLINE_MS = 45_000;

/** `companions` is NOT in GUEST_REFETCH_FIELDS but the requests grid renders
 *  it, so a reconcile that dropped it would blank the Verknüpfung cell.
 *
 *  Resolved lazily: read at module scope it would evaluate on import, which
 *  crashes any test that partially mocks GuestHandling without re-exporting the
 *  constant. */
const reconcileFields = (): string => `${GUEST_REFETCH_FIELDS ?? ""},companions`;

interface WatchedGuest {
  watches: FieldWatch[];
  deadline: number;
  refs: number;
}

interface Wave {
  guests: Map<string, WatchedGuest>;
  timer: ReturnType<typeof setTimeout> | null;
  step: number;
  delays: number[];
}

const waves = new Map<string, Wave>();

/**
 * True while a broadcast is on the stack. A listener that reacts by arming
 * again would build a loop that no deadline can stop, because each arm resets
 * the wave.
 */
let isDispatching = false;

/** Order-insensitive comparable form — a multi-select re-serialised as
 *  `["B","A"]` must not read as a change from `["A","B"]`. Mirrors
 *  `canonicalValue` in the overwrite preview, which sorts for the same reason. */
function cmp(value: unknown): string {
  if (Array.isArray(value)) return JSON.stringify([...value].map(String).sort());
  return JSON.stringify(value ?? null);
}

function readField(guest: Guest, watch: FieldWatch): unknown {
  if (watch.isAdditional) {
    const af = (guest as { additionalFields?: Record<string, unknown> })
      .additionalFields;
    return af ? af[watch.field] : undefined;
  }
  return (guest as unknown as Record<string, unknown>)[watch.field];
}

/**
 * Field-scoped, never a whole-object diff: the server echoes `updatedAt` and
 * re-serialises maps, so an object comparison fires on noise. Only the fields
 * an automation was predicted to touch count.
 */
function isSatisfied(fresh: Guest, watches: FieldWatch[]): boolean {
  if (watches.length === 0) return false;
  return watches.some((w) => {
    const now = cmp(readField(fresh, w));
    if (w.toRaw !== undefined && now === cmp(w.toRaw)) return true;
    if (w.fromRaw !== undefined && now !== cmp(w.fromRaw)) return true;
    return false;
  });
}

function emit<T>(name: string, detail: T): void {
  if (typeof window === "undefined") return;
  isDispatching = true;
  try {
    window.dispatchEvent(new CustomEvent<T>(name, { detail }));
  } finally {
    isDispatching = false;
  }
}

async function tick(eventId: string): Promise<void> {
  const wave = waves.get(eventId);
  if (!wave) return;

  const now = Date.now();
  for (const [guestId, watched] of wave.guests) {
    if (watched.deadline <= now) wave.guests.delete(guestId);
  }
  if (wave.guests.size === 0) {
    stopWave(eventId);
    return;
  }

  // A hidden tab cannot show the result anyway, and `GuestsProvider`'s focus
  // listener re-reads on return. Keep ticking so the deadline still expires.
  const hidden =
    typeof document !== "undefined" && document.visibilityState === "hidden";

  if (!hidden) {
    const ids = [...wave.guests.keys()];
    for (let i = 0; i < ids.length; i += RECONCILE_CHUNK) {
      const chunk = ids.slice(i, i + RECONCILE_CHUNK);
      try {
        const res = await getGuestsByIds(
          eventId,
          chunk,
          "",
          [],
          reconcileFields(),
          0,
          chunk.length,
        );
        if (!Array.isArray(res)) continue;
        for (const fresh of res) {
          const guestId = fresh.guestId;
          if (!guestId) continue;
          const watched = wave.guests.get(guestId);
          if (!watched) continue;
          if (!isSatisfied(fresh, watched.watches)) continue;
          wave.guests.delete(guestId);
          emit<GuestReconciledDetail>("guestReconciled", {
            eventId,
            guestId,
            guest: fresh,
          });
        }
      } catch {
        // A blip must not kill the wave — the next tick retries. Same
        // tolerance the popup's poll had.
      }
    }
  }

  if (wave.guests.size === 0) {
    stopWave(eventId);
    return;
  }
  scheduleNext(eventId);
}

function scheduleNext(eventId: string): void {
  const wave = waves.get(eventId);
  if (!wave) return;
  // Past the ramp we hold the slowest cadence until each guest's deadline.
  const delay =
    wave.delays[wave.step] ?? wave.delays[wave.delays.length - 1] ?? 10000;
  wave.step += 1;
  wave.timer = setTimeout(() => {
    void tick(eventId);
  }, delay);
}

function stopWave(eventId: string): void {
  const wave = waves.get(eventId);
  if (!wave) return;
  if (wave.timer) clearTimeout(wave.timer);
  waves.delete(eventId);
}

/**
 * Watch these guests until their fields move (or the deadline passes), then
 * broadcast `guestReconciled` with the server's row.
 *
 * Idempotent and refcounted: arming the same guest from three mounted surfaces
 * produces one fetch and three listener notifications.
 */
export function armGuestReconcile(req: ArmRequest): () => void {
  if (isDispatching) return () => {};
  if (typeof window === "undefined") return () => {};

  const { eventId, watches } = req;
  if (!eventId || watches.size === 0) return () => {};

  // A bulk action is not worth per-guest bookkeeping; pulse instead.
  if (watches.size > MAX_WATCHED_GUESTS) {
    armCoarseReconcile(eventId);
    return () => {};
  }

  const fresh = !waves.has(eventId);
  const wave: Wave = waves.get(eventId) ?? {
    guests: new Map(),
    timer: null,
    step: 0,
    delays: req.tier === "short" ? SHORT_RECONCILE_DELAYS : WORKFLOW_RECONCILE_DELAYS,
  };
  waves.set(eventId, wave);

  // A guest armed late still gets a full budget — its deadline is its own.
  const deadline = Date.now() + RECONCILE_DEADLINE_MS;
  const armedIds: string[] = [];
  for (const [guestId, fieldWatches] of watches) {
    const existing = wave.guests.get(guestId);
    if (existing) {
      for (const w of fieldWatches) {
        const dup = existing.watches.some(
          (e) => e.field === w.field && e.isAdditional === w.isAdditional,
        );
        if (!dup) existing.watches.push(w);
      }
      existing.deadline = Math.max(existing.deadline, deadline);
      existing.refs += 1;
    } else {
      wave.guests.set(guestId, {
        watches: [...fieldWatches],
        deadline,
        refs: 1,
      });
    }
    armedIds.push(guestId);
  }

  if (fresh) scheduleNext(eventId);

  return () => {
    const live = waves.get(eventId);
    if (!live) return;
    for (const guestId of armedIds) {
      const watched = live.guests.get(guestId);
      if (!watched) continue;
      watched.refs -= 1;
      if (watched.refs <= 0) live.guests.delete(guestId);
    }
    if (live.guests.size === 0) stopWave(eventId);
  };
}

/**
 * "Something landed server-side, re-pull whatever you render." Used when we
 * cannot name the affected guests: a bulk action, an unloaded workflow list, or
 * a rule whose conditions we could not evaluate honestly.
 */
/**
 * Pulse schedule for the coarse path, in ms after arming.
 *
 * Two pulses at 3s and 10s was a bet that the worker always finishes inside
 * ten seconds. It does not: a step waits on a per-guest lock and a backend
 * round-trip, and a retry starts at thirty. Missing every pulse left the 45s
 * poll as the next refresh — which is exactly the "why has it not appeared"
 * this mechanism exists to answer. The tail is spaced out rather than dense:
 * each pulse costs every mounted surface one refetch.
 */
const COARSE_PULSES_MS = [3000, 10000, 20000, 35000] as const;

/** Live coarse timers per event, so a second arm joins instead of stacking. */
const coarseTimers = new Map<string, ReturnType<typeof setTimeout>[]>();
/**
 * Has the live ladder already delivered its FIRST pulse?
 *
 * This is the predicate the restart rule is actually about, and it replaces a
 * time window that was only ever an approximation of it. The window let any arm
 * spaced `[500, 3000)` ms clear the rung that was about to fire, so an operator
 * clicking through the requests queue starved the ladder for the length of the
 * burst — while the code this replaced (`if (coarseTimers.has(eventId)) return`)
 * always let the first ladder run.
 *
 * Widening the window instead would not have fixed it: at any arm period that
 * divides the first rung evenly — 200, 500, 1000, 1500 ms — the arm lands on
 * the rung boundary and event-loop ordering decides whether the pulse survives.
 * Joining never clears anything, so the tie disappears rather than being made
 * unlikely.
 *
 * ⚠ The trade this makes, stated rather than implied: the same-write dedupe
 * effectively widens from 500 ms to the whole pre-first-rung window. A genuine
 * second write at t=1500 ms no longer restarts the ladder — it inherits the
 * pulse at 3000 ms (only 1.5 s after its own click, likely too early for the
 * worker) and the next at 10 s. No write is lost; a burst just waits longer.
 */
const coarseFirstRungFired = new Set<string>();

export function armCoarseReconcile(eventId: string): void {
  if (isDispatching || typeof window === "undefined" || !eventId) return;
  // Several surfaces degrade to a coarse pulse for the SAME write — the
  // requests grid and the companions panel both do. Without dedupe, each arm
  // added its own ladder and the grid refetched once per caller.
  //
  // But keying the dedupe on the event ALONE and never expiring it dropped a
  // genuinely new write arriving mid-ladder: it inherited whatever rungs were
  // left — often just the last one — and then silence, for exactly the change
  // the operator was watching. So a later write restarts the ladder.
  //
  // "Later" means AFTER the live ladder has delivered its first pulse. Before
  // that, an arm JOINS and clears nothing — which is what stops a burst from
  // pushing the first rung out indefinitely.
  if (coarseTimers.has(eventId) && !coarseFirstRungFired.has(eventId)) return;
  // `stopCoarseReconcile` existed for exactly this and had no caller: without
  // clearing first, the old rungs keep firing alongside the new ladder and
  // every surface refetches twice.
  stopCoarseReconcile(eventId);
  const timers = COARSE_PULSES_MS.map((delay) =>
    setTimeout(() => {
      // BEFORE the emit: `emit` flips `isDispatching` and hands control to
      // arbitrary listeners, any of which may arm again. Marking afterwards
      // would let such a re-arm see a ladder that has not "pulsed" yet and
      // join it, which is the starvation this flag exists to end.
      if (delay === COARSE_PULSES_MS[0]) coarseFirstRungFired.add(eventId);
      emit<GuestReconcileCoarseDetail>("guestReconcileCoarse", { eventId });
      const live = coarseTimers.get(eventId);
      if (live !== undefined && delay === COARSE_PULSES_MS[COARSE_PULSES_MS.length - 1]) {
        coarseTimers.delete(eventId);
        coarseFirstRungFired.delete(eventId);
      }
    }, delay),
  );
  coarseTimers.set(eventId, timers);
}

/** Cancel any pending coarse pulses for an event. */
export function stopCoarseReconcile(eventId: string): void {
  const timers = coarseTimers.get(eventId);
  if (timers === undefined) return;
  for (const t of timers) clearTimeout(t);
  coarseTimers.delete(eventId);
  coarseFirstRungFired.delete(eventId);
}

/** Test-only: waves are module state and would leak between cases. */
export function __resetReconcileScheduler(): void {
  for (const eventId of [...waves.keys()]) stopWave(eventId);
  for (const eventId of [...coarseTimers.keys()]) stopCoarseReconcile(eventId);
  coarseFirstRungFired.clear();
  isDispatching = false;
}
